/* eslint-disable react/prop-types */
import React, { Fragment, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PrintTwoToneIcon from '@mui/icons-material/PrintTwoTone'
import { cancelPrintAction, printAction } from '../../../../utils/actions'
import {
  getPrintState,
  getSettingsState,
  getUIState,
  getWallState,
} from '../../store/slices/selectors'

import { useReactToPrint } from 'react-to-print'

import { Button, PrintWrapper } from '../../../../components'
import { Result } from '../../views/Output/components'

import {
  APPLICATION_TYPES,
  APP_CONFIG,
  ROOF_MEMBER_TYPES,
  SSTHT_RAFTER,
} from '../../common/constants'
import PrintTable from './components/PrintTable'

import {
  countryOptions,
  upliftLoadDurationTypeOptions,
  downloadDurationTypeOptions,
} from '../../common/json/common.json'
import {
  applicationTypeOptions,
  memberTypeOptions as wallMemberTypeOptions,
  topPlateThicknessOptions,
  topPlateWidthOptions,
  studThicknessOptions,
  typeOptions,
} from '../../common/json/carried-member.json'
import {
  memberTypeOptions as roofMemberTypeOptions,
  roofThicknessOptions,
  roofWidthOptions,
  topChordDepthOptions,
  bottomChordDepthOptions,
  endVerticalDepthOptions,
} from '../../common/json/carrying-member.json'
import { IJOIST_INPUT_TYPE } from '../../../../utils/constants'

const getText = (list, value) => {
  const target = list?.find((item) => item.value === value)
  if (target) {
    return target.text
  } else {
    return 'Not Applicable'
  }
}

const PrintView = () => {
  const printRef = useRef()
  const dispatch = useDispatch()
  const ui = useSelector(getUIState)
  const disablePrintBtn = ui.inputErrors || ui.illustrationErrors

  const [modelImg, setModelImg] = useState()

  const handlePrintAction = useReactToPrint({
    onBeforeGetContent: async () => {
      dispatch(printAction())

      const rwsModel = document.querySelector('bp-rws')

      if (rwsModel) {
        const img = await rwsModel.handleCaptureMesh(rwsModel.handleFitCameraView)
        setModelImg(img)
      }
    },
    content: () => printRef.current,
    onAfterPrint: () => {
      setModelImg()
      dispatch(cancelPrintAction())
    },
  })

  const isUSA = useSelector((state) => getSettingsState(state).isUSA)
  const isStudMember = useSelector((state) => getWallState(state).isStudMember)

  const printData = useSelector(getPrintState)

  const settingsColumns = useMemo(
    () => [
      {
        Header: 'Country',
        accessor: 'country',
        Cell: ({ value }) => <span>{getText(countryOptions, value)}</span>,
      },
      {
        Header: 'Application Type',
        accessor: 'applicationType',
        Cell: ({ value }) => <span>{getText(applicationTypeOptions, value)}</span>,
      },
    ],
    [],
  )

  const jobSettingsColumns = useMemo(
    () => [
      {
        Header: 'Uplift Duration',
        accessor: 'upliftLoadDurationType',
        Cell: ({ value }) => (
          <span>{getText(upliftLoadDurationTypeOptions[isUSA ? 'usa' : 'canada'], value)}</span>
        ),
      },
      {
        Header: `Uplift (${isUSA ? 'ASD' : 'LSD'})`,
        accessor: 'uplift',
      },
      ...(printData.wall.applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL
        ? [
            {
              Header: 'Download Duration',
              accessor: 'downloadDurationType',
              Cell: ({ value }) => (
                <span>{getText(downloadDurationTypeOptions[isUSA ? 'usa' : 'canada'], value)}</span>
              ),
            },
            {
              Header: `Download (${isUSA ? 'ASD' : 'LSD'})`,
              accessor: 'download',
            },
          ]
        : []),
      {
        Header: 'F1',
        accessor: 'f1',
      },

      ...(printData.wall.applicationType !== APPLICATION_TYPES.ANGLE_TO_WALL
        ? [
            {
              Header:
                printData.wall.applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
                  ? 'F2 Toward'
                  : 'F2',
              accessor: 'f2',
            },
          ]
        : []),
      ...(printData.wall.applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
        ? [
            {
              Header: 'F2 Away',
              accessor: 'f2Away',
            },
            {
              Header: 'Wall Length',
              accessor: 'wallLength',
            },
          ]
        : []),

      {
        Header: 'Corrosion Resistance',
        accessor: 'corrosionResistance',
      },
    ],
    [isUSA, printData.wall.applicationType],
  )

  const wallColumns = useMemo(
    () => [
      {
        Header: 'Member Type',
        accessor: 'memberType',
        Cell: ({ value, data }) => (
          <span>
            {getText(
              wallMemberTypeOptions[data[0].applicationType] ?? wallMemberTypeOptions.default,
              value,
            )}
          </span>
        ),
      },
      {
        Header: 'Lumber Species',
        accessor: 'type',
        Cell: ({ value, data }) => (
          <span>
            {getText(
              typeOptions[data[0].memberType]
                ? typeOptions[data[0].memberType][printData.settings.country]
                : typeOptions.default,
              value,
            )}
          </span>
        ),
      },
      {
        Header: isStudMember ? 'Top Plate Thickness' : 'Depth',
        accessor: 'topPlateThickness',
        Cell: ({ value, data }) => (
          <span>
            {getText(
              topPlateThicknessOptions[data[0].memberType] ?? topPlateThicknessOptions.default,
              value,
            )}
          </span>
        ),
      },
      {
        Header: isStudMember ? 'Top Plate Width' : 'Width',
        accessor: 'topPlateWidth',
        Cell: ({ value, data }) => (
          <span>
            {getText(
              topPlateWidthOptions[data[0].memberType] ?? topPlateWidthOptions.default,
              value,
            )}
          </span>
        ),
      },
      ...(isStudMember
        ? [
            {
              Header: 'Top Plate Plies',
              accessor: 'topPlatePlies',
            },
          ]
        : []),
      ...(isStudMember && printData.wall.applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL
        ? [
            {
              Header: 'Stud Plies',
              accessor: 'studPlies',
            },
            {
              Header: 'Stud Thickness',
              accessor: 'studThickness',
              Cell: ({ value }) => <span>{getText(studThicknessOptions, value)}</span>,
            },
            {
              Header: 'Stud Alignment Offset',
              accessor: 'studAlignmentOffset',
            },
          ]
        : []),
    ],
    [isStudMember, printData.wall.applicationType, printData.settings.country],
  )

  const roofColumns = useMemo(
    () => [
      {
        Header: 'Member Type',
        accessor: 'memberType',
        Cell: ({ value }) => <span>{getText(roofMemberTypeOptions, value)}</span>,
      },
      ...(printData.roof.memberType === ROOF_MEMBER_TYPES.IJOIST
        ? printData.roof.ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
          ? [
              {
                Header: 'Manufacturer',
                accessor: 'manufacturer',
              },
              {
                Header: 'Series',
                accessor: 'series',
              },
              {
                Header: 'Model',
                accessor: 'model',
              },
              {
                Header: 'Flange Thickness',
                accessor: 'thickness',
                Cell: ({ value, data }) => (
                  <span>
                    {getText(
                      roofThicknessOptions[data[0].type] ?? roofThicknessOptions.default,
                      value,
                    )}
                  </span>
                ),
              },
            ]
          : [
              {
                Header: 'Lumber Species',
                accessor: 'lumberSpecies',
              },
              {
                Header: 'Flange Thickness',
                accessor: 'thickness',
                Cell: ({ value, data }) => (
                  <span>
                    {getText(
                      roofThicknessOptions[data[0].type] ?? roofThicknessOptions.default,
                      value,
                    )}
                  </span>
                ),
              },
            ]
        : [
            {
              Header: 'Lumber Species',
              accessor: 'lumberSpecies',
            },
          ]),
      {
        Header: printData.roof.memberType === ROOF_MEMBER_TYPES.IJOIST ? 'Flange Width' : 'Width',
        accessor: 'width',
        Cell: ({ value, data }) => (
          <span>
            {getText(
              roofWidthOptions[data[0].memberType][data[0].type] ??
                roofWidthOptions[data[0].memberType].default,
              value,
            )}
          </span>
        ),
      },
      {
        Header: 'Roof Piles',
        accessor: 'ply',
      },
      {
        Header: 'Heel Height',
        accessor: 'heel',
      },
      {
        Header:
          printData.roof.memberType === ROOF_MEMBER_TYPES.TRUSS
            ? 'Top Chord Slope/Angle'
            : 'Slope/Angle',
        accessor: 'topSlope',
      },
      ...(printData.roof.memberType === ROOF_MEMBER_TYPES.TRUSS
        ? [
            {
              Header: 'Bottom Chord Slope/Angle',
              accessor: 'bottomSlope',
            },
          ]
        : []),
      ...(!(
        printData.roof.memberType === ROOF_MEMBER_TYPES.IJOIST &&
        printData.roof.ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
      )
        ? [
            {
              Header:
                printData.roof.memberType === ROOF_MEMBER_TYPES.RAFTER ||
                (printData.roof.memberType === ROOF_MEMBER_TYPES.IJOIST &&
                  printData.roof.ijoistInputType === IJOIST_INPUT_TYPE.MANUALLY)
                  ? 'Depth'
                  : 'Top Chord Depth',
              accessor: 'topChordDepth',
              Cell: ({ value, data }) => {
                const options =
                  data[0].memberType === ROOF_MEMBER_TYPES.RAFTER
                    ? data[0].type === SSTHT_RAFTER.LVL_DFSP
                      ? topChordDepthOptions.rafter[SSTHT_RAFTER.LVL_DFSP]
                      : topChordDepthOptions.rafter[
                          data[0].width <= 3.5 ? 'lessThan' : 'greaterThan'
                        ]
                    : topChordDepthOptions[data[0].memberType]

                return <span>{getText(options, value)}</span>
              },
            },
          ]
        : []),
      ...(printData.roof.memberType === ROOF_MEMBER_TYPES.TRUSS
        ? [
            {
              Header: 'Bottom Chord Depth',
              accessor: 'bottomChordDepth',
              Cell: ({ value }) => <span>{getText(bottomChordDepthOptions, value)}</span>,
            },
            {
              Header: 'End Vertical Depth',
              accessor: 'endVerticalDepth',
              Cell: ({ value }) => <span>{getText(endVerticalDepthOptions, value)}</span>,
            },
          ]
        : []),
      {
        Header: 'Overhang',
        accessor: 'overhang',
        Cell: ({ value }) => <span>{value ? 'Yes' : 'No'}</span>,
      },
    ],
    [printData.roof.memberType, printData.roof.ijoistInputType],
  )

  return (
    <Fragment>
      <Button
        variant="topNav"
        className="button topNav__button"
        onClick={handlePrintAction}
        disabled={disablePrintBtn}
      >
        <PrintTwoToneIcon className="material-icons-two-tone material-icons-two-tone--noinvert" />
        <span className="button__text">Print</span>
      </Button>
      {modelImg && (
        <PrintWrapper ref={printRef}>
          <div className="print-header">
            <div className="appBrandingGroup">
              <img src={APP_CONFIG.logoUrl} className="iconic injectSVG appLogo" alt="TBD Logo" />
              <div className="aboutAppHeading">
                <div className="aboutAppAcronym">
                  <span className="aboutAppAcronym__label"></span>
                  {APP_CONFIG.appCode}
                </div>
                <div className="aboutAppFullName">{APP_CONFIG.appName}</div>
              </div>
            </div>
          </div>
          <div className="print-body rws__printBody">
            <div className="d-flex gap-x-2">
              <div style={{ flex: 1 }} className="space-y-4">
                <h2>Input</h2>
                <div>
                  <h3>Settings</h3>
                  <PrintTable columns={settingsColumns} data={[printData.settings]}></PrintTable>
                </div>
                <div>
                  <h3>Job Settings</h3>
                  <PrintTable
                    columns={jobSettingsColumns}
                    data={[printData.jobSettings]}
                  ></PrintTable>
                </div>
              </div>
              <div>
                <h2>3D Viewer</h2>
                <img width={300} height={300} src={modelImg} alt="3D-Viewer" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h3>Wall</h3>
                <PrintTable columns={wallColumns} data={[printData.wall]}></PrintTable>
              </div>
              <div>
                <h3>Roof</h3>
                <PrintTable columns={roofColumns} data={[printData.roof]}></PrintTable>
              </div>
            </div>
            <div className="page-break"></div>
            <div className="space-y-4">
              <h2>Output</h2>
              <div>
                <h3>Result</h3>
                <Result />
              </div>
            </div>
          </div>
        </PrintWrapper>
      )}
    </Fragment>
  )
}

export default PrintView
